<template>
  <b-container ref="dateHeader"
               class="date-header my-3 position-relative"
               :class="{ 'month-view': isMonthView }"
  >
    <div class="dates-wrapper" align-h="center">
      <div v-if="!isMonthView" class="date-item text-muted" />
      <div v-for="(d, i) in datesForDisplay"
           :key="i"
           class="date-item"
           data-test="date-item"
      >
        <!-- eslint-disable-next-line sdui/require-focus-restore-on-disabled-button -->
        <b-button
          :id="`switch-btn-day-week-${$dateFormat.date_day(d.date)}`"
          class="date-header-btn w-100 p-0 border-none"
          :class="switchDayWeekBtnClasses(d)"
          data-test="switch-btn-day-week"
          :data-cy="`switch-btn-day-week-${$dateFormat.date_day(d.date)}`"
          variant="link"
          :disabled="isDisabled"
          :aria-label="getSwitchButtonLabel(d)"
          @click="switchDayWeek(i, d)"
        >
          <slot v-if="!hideTitle" name="title">
            <span data-test="date-title-month"
                  class="gray-300 nano d-block"
                  aria-hidden="true"
                  :class="{ invisible: i !== 0 }"
            >{{
              $dateFormat.month_short(d.date)
            }}</span>
          </slot>
          <span data-test="date-title-day"
                aria-hidden="true"
                class="gray-300 nano mt-2 mb-2 d-block text-uppercase"
          >
            {{ $dateFormat.weekday_short(d.date) }}
          </span>
          <span
            v-if="!isMonthView"
            data-test="date-title-date"
            aria-hidden="true"
            class="btn-circle d-inline-flex align-items-center justify-content-center"
          >
            {{ $dateFormat.date_day(d.date) }}
          </span>
        </b-button>
      </div>
    </div>
  </b-container>
</template>

<script>
import timeGridRendering from 'src/mixins/timeGrid/timeGridRendering.js'
import { TIME_GRID } from 'src/config/constants.ts'

export default {
  name: 'TimeGridDateHeader',

  mixins: [timeGridRendering],

  props: {
    startOfWeekUnix: {
      type: Number,
      required: true,
    },

    hideTitle: {
      type: Boolean,
      default: false,
    },

    isDisabled: {
      type: Boolean,
      default: false,
    },
  },

  computed: {
    datesForDisplay() {
      return new Array(this.daysToShow).fill(null).map((el, i) => ({
        date: this.$moment.unix(this.startOfWeekUnix).clone().startOf('isoWeek').add(i, 'days'),
        i: i + 1,
      }))
    },

    current_day() {
      if (this.$moment().isoWeek() !== this.startDate.isoWeek()) return -1
      return this.$moment().isoWeekday()
    },
  },

  emits: ['dateHeader-switch-view-type'],

  methods: {
    async switchDayWeek(i, d) {
      if (this.isDisabled || (this.isScreenReaderActive && this.isActiveDate(d))) return

      const day = this.datesForDisplay[i].date.isoWeekday()
      const isSelectedDayDeselection = this.isDayView && this.startDate.isoWeekday() === day
      const date = isSelectedDayDeselection ? this.startDate.startOf('isoWeek') : this.$moment(this.datesForDisplay[i].date)
      const targetViewType = isSelectedDayDeselection ? TIME_GRID.VIEW_TYPES.WEEK : TIME_GRID.VIEW_TYPES.DAY

      // if screenreader is active, we dont let the user deselect the selected day, when clicking on the same day
      if (this.isScreenReaderActive && isSelectedDayDeselection) return

      this.updateQueryParams({
        viewType: targetViewType,
        startDate: date.format('YYYY-MM-DD'),
        showBackButton: isSelectedDayDeselection ? undefined : this.$route.query.showBackButton,
      })

      this.$emit('dateHeader-switch-view-type', targetViewType)

      // We are loosing focus, because of router.replace(). This ensures that we are focusing the clicked button again
      await this.$nextTick()
      setTimeout(() => {
        const activeSlide = document.querySelector('.swiper-slide-active')
        const activeButton = activeSlide?.querySelector('.date-header-btn.active')
        if (activeButton) activeButton.focus()
      }, 300)
    },

    switchDayWeekBtnClasses(currentDate) {
      return [
        currentDate.i === this.startDate.isoWeekday() && this.isDayView && 'active',
        currentDate.i === this.current_day && this.isWeekView && 'active',
        currentDate.i === this.current_day && 'current_day',
      ]
    },

    isActiveDate(currentDate) {
      return currentDate.i === this.startDate.isoWeekday()
    },

    getSwitchButtonLabel(date) {
      const isActive = this.isActiveDate(date)
      return `${isActive ? this.$t('accessibility.active') : ''} ${this.$dateFormat.date_daylong_dayshort_monthlong_yearlong(date.date)}`
    },
  },
}
</script>

<style scoped lang="scss">
.dates-wrapper {
  display: flex;
  align-items: center;
  justify-content: space-around;
  margin-right: -15px;
  margin-left: -15px;
}

.date-item {
  text-align: center;
  flex-basis: calc(100% / 6);
  flex-grow: 0;
  max-width: 100%;

  div {
    text-transform: uppercase;
  }
}

.btn-circle {
  height: 36px;
  width: 36px;
  /* stylelint-disable-next-line declaration-property-value-allowed-list */ // (intentionally static)
  font-size: min(1.25rem, 30px);
}

.date-header-btn:disabled {
  opacity: 1;
}

.date-header-btn .btn-circle {
  color: $color-gray-300;
}

.date-header-btn.active:not(.current_day) .btn-circle {
  border-color: $color-primary-light;
  background-color: $color-primary-light;
  color: $color-primary;
}

.current_day:not(.active) .btn-circle {
  color: $color-primary;
}

.current_day.active .btn-circle {
  border-color: $color-primary;
  background-color: $color-primary;
  color: $color-white;
}

@media only screen and (min-width: 480px) {
  .date-header {
    max-width: 1300px !important;
    padding-bottom: 4px;
  }
}

.month-view {
  .date-item {
    flex-basis: 100%;
  }
}
</style>
